import React from 'react';
import { IStorefrontSection } from '../../../../models/Storefront';
import { useGetStorefrontBundlesQuery, useGetStorefrontCategorySectionPreviewQuery, useGetStorefrontFeaturedProductsQuery, useGetStorefrontSavedSearchProductsQuery } from '../../../../store/endpoints/StorefrontProductEndpoints';
import { useResolvedSectionProducts } from './useResolvedSectionProducts';
import { MessageBar, MessageBarType } from '@fluentui/react';
import { ProductCard, BundleCard } from './ProductCard';
import { IProductCardStyles, productCardStylesFrom } from '../styleControls';
import { resolveElementStyle, SECTION_OVERRIDE_PARTS } from '../stylesConfig';
import { useStorefrontStyles } from '../StorefrontStylesContext';
import { SectionCardsContainer } from './SectionCardsContainer';

// The card styling a section contributes to the tiles inside it: its `productStyleProps` dict,
// with any card-type `$styleRef` resolved against the live Storefront Styles list. Reading the
// list through the CONTEXT (not the module registry) is what makes an unsaved Styles-panel edit
// re-render these previews — the provider's value changes, so every consumer re-renders.
const useSectionCardStyles = (section: IStorefrontSection): IProductCardStyles | undefined => {
    const { styles } = useStorefrontStyles();
    return React.useMemo(
        () => {
            const fromCardStyle = productCardStylesFrom(section.productStyleProps, styles);
            // The section's overrides for the page-level card layers (AC-1165). Resolved on their own
            // rather than merged with the page values, because the page values already reach this preview
            // through the scoped stylesheet (StorefrontHomePageEditor) — an inline style layers on top of
            // it, which is the same cascade the shopper gets.
            //
            // Under `productStyleProps` and the card style it references, which stay the more specific
            // statement about this one section's cards.
            const override = (key: string) =>
                resolveElementStyle(section.styleOverrides?.[key], SECTION_OVERRIDE_PARTS[key], styles) as
                    React.CSSProperties | undefined;
            const merged: IProductCardStyles = {
                card: { ...override('card'), ...fromCardStyle?.card },
                title: { ...override('title'), ...fromCardStyle?.title },
                price: { ...override('price'), ...fromCardStyle?.price },
                image: { ...override('image'), ...fromCardStyle?.image },
            };
            // Same contract as productCardStylesFrom: undefined when there is nothing to apply, so a
            // section with no styling of any kind renders exactly as it did before this existed.
            return Object.values(merged).some((v) => v && Object.keys(v).length > 0) ? merged : undefined;
        },
        [section.productStyleProps, section.styleOverrides, styles]);
};

interface IStorefrontSectionItemPreviewProps {
    section: IStorefrontSection;
    storefrontId?: string;
    storefrontSubUrl?: string;
    isDark?: boolean;
    // When provided (edit mode), each previewed card is clickable (with a hover pencil as the hint)
    // and opens the sections editor's quick style popover for THIS section's card styling. The
    // argument is the clicked card's root element, used as the Callout anchor.
    onEditCardStyle?: (target: HTMLElement) => void;
}

export const StorefrontSectionFeaturedProductsPreview: React.FC<IStorefrontSectionItemPreviewProps> = (props) => {
    const cardStyles = useSectionCardStyles(props.section);
    const featuredProducts = useGetStorefrontFeaturedProductsQuery({id: props.storefrontId ?? ''}, { skip: !props.storefrontId });

    if (featuredProducts.isLoading || featuredProducts.isFetching) {
        return <div>Loading...</div>;
    }

        const items = featuredProducts.data?.items?.slice(0, props.section.maxEntries) ?? [];
    return <SectionCardsContainer scroll={props.section.scrollProducts} isDark={props.isDark} itemCount={items.length}>
        {items.map((product) => (
            <ProductCard key={product.id} product={product} isDark={props.isDark} onEditStyle={props.onEditCardStyle} cardStyles={cardStyles} />
        ))}
    </SectionCardsContainer>;
}


export const StorefrontSectionBundlesPreview: React.FC<IStorefrontSectionItemPreviewProps> = (props) => {
    const cardStyles = useSectionCardStyles(props.section);
    const bundles = useGetStorefrontBundlesQuery({id: props.storefrontId ?? ''}, { skip: !props.storefrontId });

    if (bundles.isLoading || bundles.isFetching) {
        return <div>Loading...</div>;
    }

    const items = bundles.data?.items?.slice(0, props.section.maxEntries) ?? [];
    return <SectionCardsContainer scroll={props.section.scrollProducts} isDark={props.isDark} itemCount={items.length}>
        {items.map((bundle) => (
            <BundleCard key={bundle.id} bundle={bundle} isDark={props.isDark} onEditStyle={props.onEditCardStyle} cardStyles={cardStyles} />
        ))}
    </SectionCardsContainer>;
}

interface IStorefrontSectionCategoryProductsPreviewProps extends IStorefrontSectionItemPreviewProps {
    segments?: string[];
    categories?: string[];
    manufacturers?: string[];
    subCategories?: string[];
    tags?: string[];
}

export const StorefrontSectionCategoryProductsPreview: React.FC<IStorefrontSectionCategoryProductsPreviewProps> = (props) => {
    const cardStyles = useSectionCardStyles(props.section);
    const hasSegments = (props.segments?.length ?? 0) > 0;
    const hasManufacturers = (props.manufacturers?.length ?? 0) > 0;
    // Tags alone are a valid query — e.g. an "AI System" tag spanning multiple segments.
    const hasTags = (props.tags?.length ?? 0) > 0;
    const hasAnyFilter = hasSegments || hasManufacturers || hasTags;
    const products = useGetStorefrontCategorySectionPreviewQuery({id: props.storefrontId ?? '', segments: props.segments, categories: props.categories, manufacturers: props.manufacturers, subCategories: props.subCategories, tags: props.tags}, { skip: !props.storefrontId || !hasAnyFilter });

    if (!hasAnyFilter) {
        return <MessageBar messageBarType={MessageBarType.warning}>At least one product segment, manufacturer, or tag must be selected.</MessageBar>;
    }

    if (products.isLoading || products.isFetching) {
        return <div>Loading...</div>;
    }

    const items = products.data?.items?.slice(0, props.section.maxEntries) ?? [];
    return <SectionCardsContainer scroll={props.section.scrollProducts} isDark={props.isDark} itemCount={items.length}>
        {items.map((product) => (
            <ProductCard key={product.id} product={product} isDark={props.isDark} onEditStyle={props.onEditCardStyle} cardStyles={cardStyles} />
        ))}
    </SectionCardsContainer>;
}

export const StorefrontSectionProductsPreview: React.FC<IStorefrontSectionItemPreviewProps> = (props) => {
    const cardStyles = useSectionCardStyles(props.section);
    // Resolved from the section's LIVE productIds (not the saved server copy) so products appear
    // in the preview the moment they're added in the settings panel and vanish when removed.
    const { products, loading } = useResolvedSectionProducts(props.storefrontId, props.section);

    if (loading && products.length === 0) {
        return <div>Loading...</div>;
    }

    const items = products.slice(0, props.section.maxEntries);
    return <SectionCardsContainer scroll={props.section.scrollProducts} isDark={props.isDark} itemCount={items.length}>
        {items.map((product) => (
            <ProductCard key={product.id} product={product} isDark={props.isDark} onEditStyle={props.onEditCardStyle} cardStyles={cardStyles} />
        ))}
    </SectionCardsContainer>;
}

export const StorefrontSectionSavedSearchProductPreview: React.FC<IStorefrontSectionItemPreviewProps> = (props) => {
    const cardStyles = useSectionCardStyles(props.section);
    const products = useGetStorefrontSavedSearchProductsQuery({id: props.storefrontId ?? ''}, { skip: !props.storefrontId });

    if (products.isLoading || products.isFetching) {
        return <div>Loading...</div>;
    }
    
    const items = products.data?.items?.slice(0, props.section.maxEntries) ?? [];
    return <SectionCardsContainer scroll={props.section.scrollProducts} isDark={props.isDark} itemCount={items.length}>
        {items.map((product) => (
            <ProductCard key={product.id} product={product} isDark={props.isDark} onEditStyle={props.onEditCardStyle} cardStyles={cardStyles} />
        ))}
    </SectionCardsContainer>;
}
